import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { useTenant } from '../../context/TenantContext';
import {
  getTenantConfig,
  updateBranding,
  updateSso,
  updateIntegration,
  resetTenantConfig,
} from '../../services/tenantConfigStore';
import {
  getRetentionPolicy,
  updateRetentionPolicy,
  DEFAULT_POLICY,
  listErasureRequests,
  approveErasure,
  rejectErasure,
  executeErasure,
  listPurgeAudit,
  ERASURE_STATUS,
} from '../../services/dataRetentionStore';
import {
  validateRetentionPolicy,
  getMinimumRetention,
  JURISDICTION_MINIMUMS,
  placeHold,
  releaseHold,
  listHolds,
  isOnHold,
  getActiveHold,
} from '../../services/legalHoldStore';
import { hasPermission } from '../../services/rbac';

/**
 * Admin Console
 * ─────────────
 * Tenant-admin self-service surface. Three tabs:
 *   Branding    , logo upload, brand colors, display name. Live preview.
 *   SSO         , SAML / OIDC configuration. HCM launch URL generator.
 *   Integrations, per-connector field mapping, schedule, error log.
 *
 * Protected by RBAC: only admin role reaches this page (see Routes guard
 * + permission check below).
 *
 * In production, branding and SSO config changes would be approval-gated
 * (route through proposeChange like assessment status edits). For UAT we
 * apply immediately so testers can iterate quickly.
 */
const AdminConsolePage = () => {
  const navigate = useNavigate();
  const { tenant, tenantId, branding } = useTenant();
  const [activeTab, setActiveTab] = useState('branding');
  const [config, setConfig] = useState(() => getTenantConfig(tenantId));
  const [toast, setToast] = useState(null);

  // Read current user
  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); }
    catch { return {}; }
  })();
  const role = currentUser?.role;
  const canManage = role === 'admin' || role === 'approver';

  // Reload config when tenant changes
  useEffect(() => {
    setConfig(getTenantConfig(tenantId));
  }, [tenantId]);

  const flashToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4000);
  };

  // ─── Access guard ───────────────────────────────────────────────────────
  if (!canManage) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 max-w-3xl mx-auto px-6">
          <div className="bg-warning/10 border border-warning/30 rounded-lg p-6 mt-8">
            <div className="flex items-start gap-3">
              <Icon name="ShieldAlert" size={20} className="text-warning shrink-0 mt-0.5" />
              <div>
                <h2 className="font-semibold text-foreground mb-1">Admin role required</h2>
                <p className="text-sm text-muted-foreground">
                  The admin console is reserved for users with admin or approver role.
                  Your current role is <strong>{role}</strong>.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => navigate('/analytics-dashboard')}
                >
                  Back to dashboard
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-12 px-6 max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-foreground">Admin Console</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Tenant configuration for <strong>{branding.displayName}</strong>{' '}
            <span className="text-xs">({tenantId})</span>
          </p>
        </div>

        {/* Toast */}
        {toast && (
          <div className={`mb-4 px-4 py-3 rounded-md text-sm ${
            toast.type === 'success' ? 'bg-success/10 text-success border border-success/30' :
            toast.type === 'error'   ? 'bg-destructive/10 text-destructive border border-destructive/30' :
            'bg-muted text-muted-foreground border border-border'
          }`}>
            <Icon name={toast.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={14} className="inline mr-2" />
            {toast.text}
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-border mb-6">
          {[
            { id: 'branding',     label: 'Branding & Logo',  icon: 'Palette' },
            { id: 'sso',          label: 'Single Sign-On',   icon: 'KeyRound' },
            { id: 'integrations', label: 'Integrations',     icon: 'Plug' },
            { id: 'retention',    label: 'Data Retention',   icon: 'Trash2' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'branding' && (
          <BrandingTab
            tenantId={tenantId}
            config={config}
            currentBranding={branding}
            onChange={(patch) => {
              const next = updateBranding(tenantId, patch);
              setConfig((c) => ({ ...c, branding: next }));
              flashToast('success', 'Branding updated. Refresh other tabs to see changes.');
            }}
            onReset={() => {
              resetTenantConfig(tenantId);
              setConfig(getTenantConfig(tenantId));
              flashToast('success', 'Reset to default branding.');
            }}
          />
        )}

        {activeTab === 'sso' && (
          <SsoTab
            tenantId={tenantId}
            config={config}
            currentBranding={branding}
            onChange={(patch) => {
              const next = updateSso(tenantId, patch);
              setConfig((c) => ({ ...c, sso: next }));
              flashToast('success', 'SSO configuration saved.');
            }}
          />
        )}

        {activeTab === 'integrations' && (
          <IntegrationsTab
            tenantId={tenantId}
            config={config}
            onChange={(connectorId, patch) => {
              const next = updateIntegration(tenantId, connectorId, patch);
              setConfig((c) => ({
                ...c,
                integrations: { ...c.integrations, [connectorId]: next },
              }));
              flashToast('success', `${connectorId} configuration saved.`);
            }}
          />
        )}

        {activeTab === 'retention' && (
          <RetentionTab tenantId={tenantId} tenant={tenant} flashToast={flashToast} navigate={navigate} />
        )}
      </main>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// BRANDING TAB
// ────────────────────────────────────────────────────────────────────────
const BrandingTab = ({ tenantId, config, currentBranding, onChange, onReset }) => {
  const [displayName, setDisplayName] = useState(config.branding.displayName || currentBranding.displayName || '');
  const [primary, setPrimary] = useState(currentBranding.colors?.primary || '#0EA5E9');
  const [accent, setAccent] = useState(currentBranding.colors?.accent || '#6366F1');
  const [logoPreview, setLogoPreview] = useState(config.branding.logoDataUrl || currentBranding.logoUrl || null);
  const [logoError, setLogoError] = useState(null);

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLogoError(null);

    if (!/image\/(svg\+xml|png|jpeg|webp)/.test(file.type)) {
      setLogoError('Logo must be SVG, PNG, JPEG, or WebP.');
      return;
    }
    if (file.size > 500 * 1024) {
      setLogoError('Logo must be under 500 KB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setLogoPreview(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const darken = (hex, amt = 20) => {
    // simple HEX darker for hover state
    if (!/^#[0-9a-f]{6}$/i.test(hex)) return hex;
    const n = parseInt(hex.slice(1), 16);
    let r = (n >> 16) - amt; if (r < 0) r = 0;
    let g = ((n >> 8) & 0xff) - amt; if (g < 0) g = 0;
    let b = (n & 0xff) - amt; if (b < 0) b = 0;
    return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
  };

  const handleSave = () => {
    onChange({
      displayName: displayName || null,
      logoDataUrl: logoPreview,
      colors: {
        primary,
        primaryHover: darken(primary, 24),
        onPrimary: '#FFFFFF',
        accent,
      },
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Form */}
      <div className="bg-card border border-border rounded-lg p-6 space-y-5">
        <h2 className="font-semibold text-foreground">Brand Settings</h2>

        {/* Display name */}
        <Input
          label="Display name"
          description="Shown in browser tab title and email subjects"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="e.g. Acme Corporation"
        />

        {/* Logo upload */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Logo
            <span className="text-xs text-muted-foreground font-normal ml-2">
              SVG / PNG / JPEG · max 500 KB · transparent background recommended
            </span>
          </label>
          <div className="flex items-center gap-4">
            <input
              type="file"
              accept="image/svg+xml,image/png,image/jpeg,image/webp"
              onChange={handleLogoUpload}
              className="text-sm file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 file:cursor-pointer"
            />
            {logoPreview && (
              <button
                onClick={() => { setLogoPreview(null); setLogoError(null); }}
                className="text-xs text-destructive hover:underline"
              >
                Remove
              </button>
            )}
          </div>
          {logoError && <p className="text-xs text-destructive mt-1">{logoError}</p>}
        </div>

        {/* Colors */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Primary color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={primary}
                onChange={(e) => setPrimary(e.target.value)}
                className="w-12 h-10 rounded border border-border cursor-pointer"
              />
              <input
                type="text"
                value={primary}
                onChange={(e) => setPrimary(e.target.value)}
                className="flex-1 px-2 py-2 text-sm font-mono bg-background border border-border rounded-md"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Accent color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={accent}
                onChange={(e) => setAccent(e.target.value)}
                className="w-12 h-10 rounded border border-border cursor-pointer"
              />
              <input
                type="text"
                value={accent}
                onChange={(e) => setAccent(e.target.value)}
                className="flex-1 px-2 py-2 text-sm font-mono bg-background border border-border rounded-md"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <Button variant="default" iconName="Save" onClick={handleSave}>
            Save Branding
          </Button>
          <Button variant="ghost" onClick={onReset}>
            Reset to Default
          </Button>
        </div>

        <div className="text-xs text-muted-foreground bg-muted/40 rounded p-3 leading-relaxed">
          <Icon name="Info" size={12} className="inline mr-1" />
          Changes apply across the entire tenant immediately after saving.
          Refresh any open browser tabs to see the new logo and colors. In production
          this is gated by the approver role to prevent accidental rebrands.
        </div>
      </div>

      {/* Live preview */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="font-semibold text-foreground mb-4">Live Preview</h2>

        <div
          className="rounded-lg overflow-hidden border border-border"
          style={{ backgroundColor: '#FAFAFA' }}
        >
          {/* Mock header */}
          <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {logoPreview ? (
                <img src={logoPreview} alt="logo preview" className="h-8 max-w-[160px] object-contain" />
              ) : (
                <span className="text-lg font-semibold text-gray-700">{displayName || 'Your Brand'}</span>
              )}
            </div>
            <span className="text-xs text-gray-400">{tenantId}.clearequal.com</span>
          </div>

          {/* Mock dashboard card */}
          <div className="p-5 space-y-3">
            <h3 className="font-semibold text-gray-800 text-sm">Pay Equity Dashboard</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded p-3 border border-gray-200">
                <p className="text-xs text-gray-500 uppercase">Mean Pay Gap</p>
                <p className="text-2xl font-bold mt-1" style={{ color: primary }}>3.2%</p>
              </div>
              <div className="bg-white rounded p-3 border border-gray-200">
                <p className="text-xs text-gray-500 uppercase">Compliance</p>
                <p className="text-2xl font-bold mt-1" style={{ color: accent }}>94/100</p>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button
                className="px-4 py-2 rounded-md text-sm font-medium text-white"
                style={{ backgroundColor: primary }}
              >
                Generate Report
              </button>
              <button
                className="px-4 py-2 rounded-md text-sm font-medium border"
                style={{ borderColor: primary, color: primary, background: 'white' }}
              >
                View Details
              </button>
            </div>
          </div>
        </div>

        <p className="text-xs text-muted-foreground mt-4 italic">
          Preview updates as you edit. Click Save Branding to apply for real.
        </p>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// SSO TAB
// ────────────────────────────────────────────────────────────────────────
const SsoTab = ({ tenantId, config, currentBranding, onChange }) => {
  const sso = config.sso || {};
  const [provider, setProvider] = useState(sso.provider || '');
  const [entryPoint, setEntryPoint] = useState(sso.entryPoint || '');
  const [certificate, setCertificate] = useState(sso.certificate || '');
  const [allowedDomains, setAllowedDomains] = useState((sso.allowedDomains || []).join(', '));
  const [launchEnabled, setLaunchEnabled] = useState(!!sso.launchEnabled);

  const subdomain = currentBranding.subdomain || tenantId;
  const baseUrl = `https://${subdomain}.clearequal.com`;
  const launchUrl = `${baseUrl}/sso/launch`;
  const acsUrl = `${baseUrl}/sso/saml/acs`;
  const metadataUrl = `${baseUrl}/sso/saml/metadata.xml`;
  const issuer = sso.issuer || `${baseUrl}/sso`;

  const samlMetadata = `<?xml version="1.0"?>
<EntityDescriptor entityID="${issuer}"
  xmlns="urn:oasis:names:tc:SAML:2.0:metadata">
  <SPSSODescriptor protocolSupportEnumeration="urn:oasis:names:tc:SAML:2.0:protocol"
    AuthnRequestsSigned="true" WantAssertionsSigned="true">
    <AssertionConsumerService
      Binding="urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST"
      Location="${acsUrl}"
      index="0" isDefault="true"/>
    <NameIDFormat>urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress</NameIDFormat>
  </SPSSODescriptor>
</EntityDescriptor>`;

  const handleSave = () => {
    onChange({
      provider: provider || null,
      entryPoint,
      certificate,
      launchEnabled,
      allowedDomains: allowedDomains.split(',').map((d) => d.trim()).filter(Boolean),
    });
  };

  const copy = (text) => {
    try { navigator.clipboard.writeText(text); } catch (_) {}
  };

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6 space-y-5">
        <div>
          <h2 className="font-semibold text-foreground">Identity Provider</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure your IdP (Okta, Azure AD, Google Workspace, OneLogin, etc.) so employees sign
            in once at your company gateway and land directly in {currentBranding.displayName} without
            a second password.
          </p>
        </div>

        <Select
          label="SSO Protocol"
          options={[
            { value: '',     label: ', Select protocol,' },
            { value: 'saml', label: 'SAML 2.0' },
            { value: 'oidc', label: 'OpenID Connect (OIDC)' },
          ]}
          value={provider}
          onChange={setProvider}
        />

        {provider && (
          <>
            <Input
              label={provider === 'saml' ? 'IdP SSO URL' : 'Authorization endpoint'}
              description={provider === 'saml'
                ? 'The HTTP-POST or HTTP-Redirect SSO endpoint from your IdP metadata'
                : 'OIDC authorization endpoint from your provider'}
              value={entryPoint}
              onChange={(e) => setEntryPoint(e.target.value)}
              placeholder={provider === 'saml'
                ? 'https://idp.acme.com/saml/sso'
                : 'https://idp.acme.com/oauth2/authorize'}
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                {provider === 'saml' ? 'IdP Signing Certificate (X.509 PEM)' : 'OIDC Public Key (JWKS or PEM)'}
              </label>
              <textarea
                value={certificate}
                onChange={(e) => setCertificate(e.target.value)}
                rows={6}
                placeholder="-----BEGIN CERTIFICATE-----&#10;MIIDXTCCAk...&#10;-----END CERTIFICATE-----"
                className="w-full px-3 py-2 text-xs font-mono bg-background border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <Input
              label="Allowed email domains"
              description="Comma-separated list. Only emails from these domains can sign in via SSO."
              value={allowedDomains}
              onChange={(e) => setAllowedDomains(e.target.value)}
              placeholder="acme.com, acme-eu.com"
            />

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={launchEnabled}
                onChange={(e) => setLaunchEnabled(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-sm">
                <strong className="block text-foreground">Enable HCM-launched SSO</strong>
                <span className="text-muted-foreground">
                  Permits Workday, SAP SuccessFactors, BambooHR and other HCM systems to launch{' '}
                  {currentBranding.displayName} via SP-initiated or IdP-initiated flow.
                </span>
              </span>
            </label>

            <Button variant="default" iconName="Save" onClick={handleSave}>
              Save SSO Configuration
            </Button>
          </>
        )}
      </div>

      {/* Service Provider details, what to give to the IdP / HCM admin */}
      {provider && (
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <div>
            <h2 className="font-semibold text-foreground">Service Provider Details</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Send these values to your IdP administrator to register {currentBranding.displayName}.
            </p>
          </div>

          <SsoField label="Entity ID / Issuer" value={issuer} onCopy={() => copy(issuer)} />
          <SsoField label="ACS URL (Reply URL)" value={acsUrl} onCopy={() => copy(acsUrl)} />
          <SsoField label="Metadata URL" value={metadataUrl} onCopy={() => copy(metadataUrl)} />
          <SsoField label="HCM Launch URL" value={launchUrl} onCopy={() => copy(launchUrl)} />

          {provider === 'saml' && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-foreground">SAML Metadata XML</label>
                <Button variant="ghost" size="sm" iconName="Copy" onClick={() => copy(samlMetadata)}>
                  Copy
                </Button>
              </div>
              <pre className="text-xs font-mono bg-muted p-3 rounded border border-border overflow-x-auto leading-relaxed">
                {samlMetadata}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* HCM Launch configuration help */}
      {provider && launchEnabled && (
        <div className="bg-primary/5 border border-primary/30 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Icon name="Rocket" size={20} className="text-primary shrink-0 mt-0.5" />
            <div className="space-y-3 flex-1">
              <h3 className="font-semibold text-foreground">HCM Launch, How to Configure</h3>
              <p className="text-sm text-muted-foreground">
                Add {currentBranding.displayName} as an external app in your HCM system using the
                launch URL above. The HCM passes a signed JWT in the request, ClearEqual validates
                it against your IdP certificate, and the user lands authenticated in their portal.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                <HcmCard
                  title="Workday"
                  steps={[
                    'Navigate to: Maintain Tenant Setup',
                    'Add "External App" entry',
                    'Paste Launch URL above',
                    'Map: Worker.email → SSO subject',
                  ]}
                />
                <HcmCard
                  title="SAP SuccessFactors"
                  steps={[
                    'Provisioning → SSO Settings',
                    'Add ClearEqual as IdP-initiated app',
                    'Use ACS URL above',
                    'Set NameID = email',
                  ]}
                />
                <HcmCard
                  title="BambooHR / Personio"
                  steps={[
                    'Settings → Apps → Custom SSO',
                    'Use Metadata URL for auto-config',
                    'Or paste XML manually',
                    'Test with admin account first',
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SsoField = ({ label, value, onCopy }) => (
  <div>
    <label className="text-sm font-medium text-foreground">{label}</label>
    <div className="flex items-center gap-2 mt-1">
      <input
        readOnly
        value={value}
        className="flex-1 px-3 py-2 text-xs font-mono bg-muted/50 border border-border rounded-md text-muted-foreground"
      />
      <Button variant="ghost" size="sm" iconName="Copy" onClick={onCopy} />
    </div>
  </div>
);

const HcmCard = ({ title, steps }) => (
  <div className="bg-background border border-border rounded p-3">
    <h4 className="font-semibold text-sm text-foreground mb-2">{title}</h4>
    <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
      {steps.map((s, i) => <li key={i}>{s}</li>)}
    </ol>
  </div>
);

// ────────────────────────────────────────────────────────────────────────
// INTEGRATIONS TAB
// ────────────────────────────────────────────────────────────────────────
const CONNECTOR_META = {
  sap_successfactors: { name: 'SAP SuccessFactors', icon: 'Cloud', vendor: 'SAP' },
  workday:            { name: 'Workday HCM',         icon: 'Building2', vendor: 'Workday' },
  personio:           { name: 'Personio',            icon: 'Users', vendor: 'Personio SE' },
  bamboohr:           { name: 'BambooHR',            icon: 'Leaf', vendor: 'Bamboo HR LLC' },
  csv_upload:         { name: 'CSV Upload',          icon: 'FileSpreadsheet', vendor: 'Manual' },
};

const IntegrationsTab = ({ tenantId, config, onChange }) => {
  const [selectedConnector, setSelectedConnector] = useState(null);

  if (selectedConnector) {
    const conn = config.integrations[selectedConnector];
    const meta = CONNECTOR_META[selectedConnector];
    return (
      <ConnectorDetail
        connectorId={selectedConnector}
        meta={meta}
        config={conn}
        onBack={() => setSelectedConnector(null)}
        onChange={(patch) => onChange(selectedConnector, patch)}
      />
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-6 border-b border-border">
        <h2 className="font-semibold text-foreground">Connectors</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Configure how {tenantId} connects to source HR systems. Click any connector to drill into
          field mappings, sync schedule, and error logs.
        </p>
      </div>

      <div className="divide-y divide-border">
        {Object.entries(CONNECTOR_META).map(([id, meta]) => {
          const c = config.integrations[id] || {};
          return (
            <button
              key={id}
              onClick={() => setSelectedConnector(id)}
              className="w-full flex items-center gap-4 p-5 hover:bg-muted/30 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded bg-muted flex items-center justify-center shrink-0">
                <Icon name={meta.icon} size={20} className="text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-foreground">{meta.name}</h3>
                <p className="text-xs text-muted-foreground">{meta.vendor}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {c.connected ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-success/10 text-success">
                    <Icon name="CheckCircle" size={12} />
                    Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-muted text-muted-foreground">
                    Not connected
                  </span>
                )}
                {c.errors && c.errors.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-destructive/10 text-destructive">
                    <Icon name="AlertCircle" size={12} />
                    {c.errors.length} error{c.errors.length === 1 ? '' : 's'}
                  </span>
                )}
                <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ConnectorDetail = ({ connectorId, meta, config, onBack, onChange }) => {
  const [fieldMap, setFieldMap] = useState(config.fieldMap || []);
  const [schedule, setSchedule] = useState(config.schedule || 'daily');

  const updateRow = (idx, patch) => {
    const next = [...fieldMap];
    next[idx] = { ...next[idx], ...patch };
    setFieldMap(next);
  };

  const handleSave = () => {
    onChange({ fieldMap, schedule });
  };

  const handleTestConnection = () => {
    onChange({
      connected: true,
      lastSyncAt: new Date().toISOString(),
      errors: [],
    });
  };

  const requiredMissing = fieldMap.filter((f) => f.required && !f.sourceField).length;

  return (
    <div className="space-y-6">
      <Button variant="ghost" iconName="ArrowLeft" onClick={onBack}>
        Back to connectors
      </Button>

      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded bg-muted flex items-center justify-center">
              <Icon name={meta.icon} size={24} className="text-muted-foreground" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground text-lg">{meta.name}</h2>
              <p className="text-sm text-muted-foreground">{meta.vendor}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {config.connected ? (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-success/10 text-success">
                <Icon name="CheckCircle" size={14} />
                Connected
              </span>
            ) : (
              <Button variant="default" iconName="Link" onClick={handleTestConnection}>
                Connect
              </Button>
            )}
          </div>
        </div>

        {config.lastSyncAt && (
          <p className="text-xs text-muted-foreground">
            Last sync: {new Date(config.lastSyncAt).toLocaleString('en-GB')}
          </p>
        )}
      </div>

      {/* Field mapping */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-foreground">Field Mapping</h3>
            <p className="text-sm text-muted-foreground">
              Map fields from {meta.name} to ClearEqual's data model.
            </p>
          </div>
          {requiredMissing > 0 && (
            <span className="px-2 py-1 rounded text-xs bg-warning/10 text-warning">
              {requiredMissing} required field{requiredMissing === 1 ? '' : 's'} unmapped
            </span>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase text-muted-foreground border-b border-border">
                <th className="text-left py-2 pr-4">Source Field ({meta.name})</th>
                <th className="text-left py-2 pr-4">Target Field (ClearEqual)</th>
                <th className="text-left py-2 pr-4">Required</th>
                <th className="text-left py-2">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {fieldMap.map((row, idx) => (
                <tr key={idx}>
                  <td className="py-2 pr-4">
                    <input
                      value={row.sourceField || ''}
                      onChange={(e) => updateRow(idx, { sourceField: e.target.value })}
                      className="w-full px-2 py-1 text-sm font-mono bg-background border border-border rounded"
                    />
                  </td>
                  <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">
                    {row.targetField}
                  </td>
                  <td className="py-2 pr-4">
                    {row.required ? (
                      <span className="text-xs px-2 py-0.5 rounded bg-destructive/10 text-destructive">required</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">optional</span>
                    )}
                  </td>
                  <td className="py-2 text-xs text-muted-foreground">{row.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sync schedule */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold text-foreground mb-4">Sync Schedule</h3>
        <Select
          options={[
            { value: 'manual',  label: 'Manual only, sync on demand' },
            { value: 'hourly',  label: 'Every hour' },
            { value: 'daily',   label: 'Daily, overnight (recommended)' },
            { value: 'weekly',  label: 'Weekly, Sunday night' },
          ]}
          value={schedule}
          onChange={setSchedule}
        />
      </div>

      {/* Error log */}
      {config.errors && config.errors.length > 0 && (
        <div className="bg-card border border-destructive/30 rounded-lg p-6">
          <h3 className="font-semibold text-destructive mb-3">Recent Errors</h3>
          <div className="space-y-2">
            {config.errors.slice(0, 5).map((err, i) => (
              <div key={i} className="text-sm text-muted-foreground bg-destructive/5 p-3 rounded">
                <p className="font-mono text-xs">{err.code}</p>
                <p>{err.message}</p>
                <p className="text-xs mt-1">{err.timestamp}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button variant="default" iconName="Save" onClick={handleSave}>
          Save Configuration
        </Button>
        <Button variant="ghost" onClick={onBack}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// DATA RETENTION TAB, GDPR Art. 5(1)(e) policy + Art. 17 erasure queue
// ────────────────────────────────────────────────────────────────────────
const CATEGORY_LABELS = {
  contact_data:        'Contact data (name, email, address)',
  compensation_data:   'Compensation history (salary, bonus)',
  cv_profile:          'CV / skills profile',
  info_requests:       'Article 7 information requests',
  pay_equity_stats:    'Pay equity statistics (anonymised)',
  ai_audit_log:        'AI audit log (decision records)',
  audit_trail:         'System audit trail',
};

const RetentionTab = ({ tenantId, tenant, flashToast, navigate }) => {
  const [activeSubTab, setActiveSubTab] = useState('policy');
  const [policy, setPolicy] = useState(() => getRetentionPolicy(tenantId));
  const [requests, setRequests] = useState(() => listErasureRequests({ tenantId }));
  const [audit, setAudit] = useState(() => listPurgeAudit({ tenantId }));
  const [holds, setHolds] = useState(() => listHolds({ tenantId }));

  const jurisdiction = tenant?.countryCode || null;
  const policyWarnings = useMemo(
    () => validateRetentionPolicy(policy, jurisdiction),
    [policy, jurisdiction]
  );

  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); }
    catch { return {}; }
  })();
  const currentUserId = currentUser?.email;
  const role = currentUser?.role;
  const isApprover = role === 'approver';
  const isAdmin = role === 'admin';

  const refresh = () => {
    setPolicy(getRetentionPolicy(tenantId));
    setRequests(listErasureRequests({ tenantId }));
    setAudit(listPurgeAudit({ tenantId }));
    setHolds(listHolds({ tenantId }));
  };

  useEffect(() => {
    refresh();
  }, [tenantId]);

  const updateCategoryDays = (cat, newDays) => {
    const num = Math.max(0, parseInt(newDays, 10) || 0);
    const next = updateRetentionPolicy(tenantId, {
      [cat]: { ...policy[cat], retentionDays: num },
    });
    setPolicy(next);
  };

  const handleApprove = (req) => {
    try {
      approveErasure(req.id, currentUserId, '');
      refresh();
      flashToast('success', 'Erasure approved. Purge will run on next sweep, or click "Execute now".');
    } catch (e) {
      flashToast('error', e.message);
    }
  };

  const handleReject = (req) => {
    const reason = prompt(
      'Reason for rejection (required by GDPR Art. 12(4)):\n\nCommon reasons:\n• Legal hold\n• Tax retention obligation\n• Pending litigation'
    );
    if (!reason) return;
    try {
      rejectErasure(req.id, currentUserId, reason);
      refresh();
      flashToast('success', 'Erasure rejected. Data subject must be notified within 30 days.');
    } catch (e) {
      flashToast('error', e.message);
    }
  };

  const handleExecute = (req) => {
    if (!confirm(
      `This will permanently delete personal data for ${req.subjectEmail}.\n\nThis action cannot be undone.\n\nProceed?`
    )) return;
    try {
      executeErasure(req.id, currentUserId);
      refresh();
      flashToast('success', 'Erasure executed. See audit log for details.');
    } catch (e) {
      flashToast('error', e.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex border-b border-border">
        {[
          { id: 'policy',   label: 'Retention Policy',  count: null,                                 icon: 'ClipboardList' },
          { id: 'requests', label: 'Erasure Requests',  count: requests.filter((r) => r.status === ERASURE_STATUS.PENDING).length, icon: 'UserX' },
          { id: 'holds',    label: 'Legal Holds',       count: holds.filter((h) => h.status === 'active').length, icon: 'ShieldAlert' },
          { id: 'audit',    label: 'Purge Audit Log',   count: audit.length,                         icon: 'History' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 ${
              activeSubTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab.icon} size={14} />
            {tab.label}
            {tab.count !== null && tab.count > 0 && (
              <span className="ml-1 text-xs px-1.5 py-0.5 bg-warning/10 text-warning rounded">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* POLICY SUB-TAB */}
      {activeSubTab === 'policy' && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="mb-4">
            <h2 className="font-semibold text-foreground">Retention Policy</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Days to keep each data category after employee exit. After the window elapses,
              the auto-purge job removes (or anonymises) the records.
            </p>
            <p className="text-xs text-muted-foreground mt-2 italic">
              GDPR Art. 5(1)(e) requires personal data not be kept "longer than necessary".
              {jurisdiction && JURISDICTION_MINIMUMS[jurisdiction] && (
                <> Minimums for {jurisdiction} jurisdiction are enforced below.</>
              )}
            </p>
          </div>

          {/* Jurisdiction warnings */}
          {policyWarnings.length > 0 && (
            <div className="mb-4 bg-destructive/5 border border-destructive/30 rounded-md p-4">
              <div className="flex items-start gap-2">
                <Icon name="AlertTriangle" size={16} className="text-destructive shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-destructive text-sm mb-2">
                    Below-minimum retention warnings ({jurisdiction})
                  </h3>
                  <ul className="space-y-1 text-xs text-foreground">
                    {policyWarnings.map((w, i) => (
                      <li key={i}>
                        <strong>{CATEGORY_LABELS[w.category] || w.category}:</strong>{' '}
                        you set {w.requested} days but {w.jurisdiction} law requires{' '}
                        <strong>{w.minimum} days</strong> (~{(w.minimum / 365).toFixed(1)} years).
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-muted-foreground mt-2 italic">
                    Saving below the legal minimum exposes the tenant to regulatory penalties.
                    Consult counsel before proceeding.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-muted-foreground border-b border-border">
                  <th className="text-left py-2 pr-4">Data Category</th>
                  <th className="text-left py-2 pr-4">Retention (days)</th>
                  <th className="text-left py-2 pr-4">{jurisdiction} Minimum</th>
                  <th className="text-left py-2 pr-4">Action</th>
                  <th className="text-left py-2">Legal Basis</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {Object.entries(policy).map(([cat, cfg]) => (
                  <tr key={cat}>
                    <td className="py-3 pr-4">
                      <p className="font-medium text-foreground">{CATEGORY_LABELS[cat] || cat}</p>
                      <p className="text-xs text-muted-foreground font-mono">{cat}</p>
                    </td>
                    <td className="py-3 pr-4">
                      <input
                        type="number"
                        min="0"
                        value={cfg.retentionDays}
                        onChange={(e) => updateCategoryDays(cat, e.target.value)}
                        className="w-24 px-2 py-1 text-sm bg-background border border-border rounded"
                      />
                      <p className="text-xs text-muted-foreground mt-0.5">
                        ≈ {(cfg.retentionDays / 365).toFixed(1)} years
                      </p>
                    </td>
                    <td className="py-3 pr-4 text-xs">
                      {(() => {
                        const min = jurisdiction ? getMinimumRetention(jurisdiction, cat) : 0;
                        if (min === 0) return <span className="text-muted-foreground">,</span>;
                        const below = cfg.retentionDays < min && cfg.action === 'delete';
                        return (
                          <span className={below ? 'text-destructive font-semibold' : 'text-muted-foreground'}>
                            {min} days
                            {below && <Icon name="AlertCircle" size={12} className="inline ml-1" />}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="py-3 pr-4">
                      {cfg.action === 'delete' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-destructive/10 text-destructive">
                          <Icon name="Trash2" size={12} />
                          Delete
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-primary/10 text-primary">
                          <Icon name="EyeOff" size={12} />
                          Anonymise
                        </span>
                      )}
                    </td>
                    <td className="py-3 text-xs text-muted-foreground leading-relaxed">
                      {cfg.legalBasis}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-xs bg-warning/10 border border-warning/30 rounded p-3 leading-relaxed">
            <Icon name="AlertTriangle" size={12} className="inline mr-1 text-warning" />
            <strong className="text-warning">Important:</strong> Some categories have legal minimum
            retention periods (tax records: typically 5-10 years; SOC 2 audit trail: 7 years).
            Setting retention shorter than the legal minimum may put the organisation in breach of
            tax or accountability obligations. Consult your DPO and tax advisor.
          </div>

          <div className="mt-4">
            <Button
              variant="ghost"
              iconName="RotateCcw"
              size="sm"
              onClick={() => {
                if (!confirm('Reset all categories to default retention periods?')) return;
                Object.keys(DEFAULT_POLICY).forEach((cat) => {
                  updateRetentionPolicy(tenantId, { [cat]: DEFAULT_POLICY[cat] });
                });
                setPolicy(getRetentionPolicy(tenantId));
                flashToast('success', 'Reset to default retention policy.');
              }}
            >
              Reset to defaults
            </Button>
          </div>
        </div>
      )}

      {/* REQUESTS SUB-TAB */}
      {activeSubTab === 'requests' && (
        <div className="space-y-4">
          {requests.length === 0 && (
            <div className="bg-card border border-dashed border-border rounded-lg p-10 text-center">
              <Icon name="UserCheck" size={28} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No erasure requests on file.</p>
              <p className="text-xs text-muted-foreground mt-1">
                Employees can request data deletion from their portal under Settings → Privacy.
              </p>
            </div>
          )}

          {requests.map((req) => (
            <ErasureRequestCard
              key={req.id}
              req={req}
              isApprover={isApprover}
              currentUserId={currentUserId}
              onApprove={() => handleApprove(req)}
              onReject={() => handleReject(req)}
              onExecute={() => handleExecute(req)}
            />
          ))}
        </div>
      )}

      {/* LEGAL HOLDS SUB-TAB */}
      {activeSubTab === 'holds' && (
        <LegalHoldsPanel
          tenantId={tenantId}
          holds={holds}
          currentUserId={currentUserId}
          isApprover={isApprover}
          isAdmin={isAdmin}
          onChange={refresh}
          flashToast={flashToast}
        />
      )}

      {/* AUDIT SUB-TAB */}
      {activeSubTab === 'audit' && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="font-semibold text-foreground">Purge Audit Log</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Immutable record of every data purge, required by GDPR Art. 5(2) accountability.
              Most-recent first.
            </p>
          </div>

          {audit.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No purges recorded yet.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {audit.map((entry, i) => (
                <div key={i} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-foreground text-sm">
                      {entry.subjectEmail}
                    </p>
                    <span className="text-xs text-muted-foreground">
                      {new Date(entry.timestamp).toLocaleString('en-GB')}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-1">
                    By <span className="text-foreground">{entry.actor}</span> · {entry.reason}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {entry.stores.map((s, j) => (
                      <span
                        key={j}
                        className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground"
                      >
                        {s.store}: {s.removed} {s.anonymised ? '(anonymised)' : 'removed'}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ErasureRequestCard = ({ req, isApprover, currentUserId, onApprove, onReject, onExecute }) => {
  const isPending = req.status === ERASURE_STATUS.PENDING;
  const isApproved = req.status === ERASURE_STATUS.APPROVED;
  const isSelfSubmitted = req.submittedBy === currentUserId;
  const deadline = new Date(req.deadline);
  const daysRemaining = Math.ceil((deadline - Date.now()) / (24 * 60 * 60 * 1000));
  const isOverdue = daysRemaining < 0 && isPending;

  const statusColor = {
    [ERASURE_STATUS.PENDING]:   'bg-warning/10 text-warning',
    [ERASURE_STATUS.APPROVED]:  'bg-primary/10 text-primary',
    [ERASURE_STATUS.REJECTED]:  'bg-destructive/10 text-destructive',
    [ERASURE_STATUS.EXECUTED]:  'bg-success/10 text-success',
    [ERASURE_STATUS.CANCELLED]: 'bg-muted text-muted-foreground',
  }[req.status];

  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-foreground">{req.subjectName || req.subjectEmail}</h3>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColor}`}>
              {req.status}
            </span>
            {isOverdue && (
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-destructive text-destructive-foreground">
                OVERDUE
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground font-mono">{req.subjectEmail}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Submitted {new Date(req.submittedAt).toLocaleDateString('en-GB')}</p>
          {isPending && (
            <p className="text-xs text-muted-foreground mt-1">
              {daysRemaining >= 0
                ? `Deadline: ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}`
                : `Overdue by ${-daysRemaining} day${-daysRemaining === 1 ? '' : 's'}`}
            </p>
          )}
        </div>
      </div>

      {req.reason && (
        <p className="text-sm text-muted-foreground italic mb-2">"{req.reason}"</p>
      )}

      <p className="text-xs text-muted-foreground mb-3">
        Scope: {req.scope.includes('all') ? 'All categories' : req.scope.join(', ')}
      </p>

      {req.status === ERASURE_STATUS.REJECTED && req.rejectionReason && (
        <div className="bg-destructive/5 border border-destructive/30 rounded p-3 text-sm mb-3">
          <p className="text-xs uppercase text-destructive mb-1">Rejection reason</p>
          <p className="text-foreground">{req.rejectionReason}</p>
        </div>
      )}

      {req.status === ERASURE_STATUS.EXECUTED && req.purgeSummary && (
        <div className="bg-success/5 border border-success/30 rounded p-3 text-sm mb-3">
          <p className="text-xs uppercase text-success mb-1">Purge summary</p>
          <p className="text-xs text-muted-foreground">
            Executed {new Date(req.executedAt).toLocaleString('en-GB')} by {req.executedBy}.
          </p>
          <div className="flex flex-wrap gap-1 mt-2">
            {req.purgeSummary.stores.map((s, i) => (
              <span key={i} className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">
                {s.store}: {s.removed} {s.anonymised ? '(anonymised)' : 'removed'}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Action buttons */}
      {isPending && isApprover && !isSelfSubmitted && (
        <div className="flex gap-2 pt-2 border-t border-border mt-3">
          <Button variant="default" size="sm" iconName="Check" onClick={onApprove}>
            Approve Erasure
          </Button>
          <Button variant="outline" size="sm" iconName="X" onClick={onReject}>
            Reject (with reason)
          </Button>
        </div>
      )}

      {isPending && isSelfSubmitted && (
        <p className="text-xs text-warning bg-warning/10 rounded p-2 mt-2">
          <Icon name="AlertTriangle" size={12} className="inline mr-1" />
          You submitted this request, a different approver must review it (two-person rule).
        </p>
      )}

      {isApproved && (
        <div className="pt-2 border-t border-border mt-3">
          <Button variant="destructive" size="sm" iconName="Trash2" onClick={onExecute}>
            Execute purge now
          </Button>
          <p className="text-xs text-muted-foreground mt-2">
            Or wait, auto-purge runs on next scheduled sweep.
          </p>
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// LEGAL HOLDS PANEL, DPO marks subjects as exempt from auto-purge & erasure
// ────────────────────────────────────────────────────────────────────────
const LegalHoldsPanel = ({ tenantId, holds, currentUserId, isApprover, isAdmin, onChange, flashToast }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    subjectEmail: '',
    legalBasis: 'litigation',
    reason: '',
    expectedReleaseDate: '',
  });

  const canPlace = isApprover || isAdmin;
  const activeHolds = holds.filter((h) => h.status === 'active');
  const releasedHolds = holds.filter((h) => h.status === 'released');

  const handlePlace = () => {
    if (!form.subjectEmail) {
      flashToast('error', 'Subject email is required.');
      return;
    }
    if (!form.reason.trim()) {
      flashToast('error', 'A documented reason is required (Art. 17(3) justification).');
      return;
    }
    try {
      placeHold({
        subjectEmail: form.subjectEmail.trim().toLowerCase(),
        tenantId,
        placedBy: currentUserId,
        reason: form.reason,
        expectedReleaseDate: form.expectedReleaseDate || null,
        legalBasis: form.legalBasis,
      });
      setForm({ subjectEmail: '', legalBasis: 'litigation', reason: '', expectedReleaseDate: '' });
      setShowForm(false);
      onChange();
      flashToast('success', `Hold placed on ${form.subjectEmail}. Auto-purge and erasure are now blocked for this subject.`);
    } catch (err) {
      flashToast('error', err.message);
    }
  };

  const handleRelease = (hold) => {
    if (hold.placedBy === currentUserId) {
      flashToast('error', 'A different user must release this hold (separation of duties).');
      return;
    }
    const note = prompt('Optional release note (e.g. "litigation settled, court order ref XYZ"):') || '';
    try {
      releaseHold(hold.id, currentUserId, note);
      onChange();
      flashToast('success', `Hold on ${hold.subjectEmail} released. Auto-purge and erasure now resume normal flow.`);
    } catch (err) {
      flashToast('error', err.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-semibold text-foreground">Legal Holds</h2>
            <p className="text-sm text-muted-foreground mt-1">
              GDPR Art. 17(3) exempts personal data from erasure when retention is necessary for
              compliance with a legal obligation or for legal claims. Place a subject on hold to
              suspend auto-purge and refuse new erasure requests until the hold is released.
            </p>
          </div>
          {canPlace && !showForm && (
            <Button variant="default" size="sm" iconName="ShieldAlert" onClick={() => setShowForm(true)}>
              Place hold
            </Button>
          )}
        </div>

        {showForm && (
          <div className="mt-4 bg-muted/50 border border-border rounded p-4 space-y-3">
            <Input
              label="Subject email"
              value={form.subjectEmail}
              onChange={(e) => setForm({ ...form, subjectEmail: e.target.value })}
              placeholder="employee@company.com"
            />
            <div className="grid grid-cols-2 gap-3">
              <Select
                label="Legal basis"
                options={[
                  { value: 'litigation',           label: 'Active or anticipated litigation' },
                  { value: 'tax_audit',            label: 'Tax authority audit' },
                  { value: 'regulatory_inquiry',   label: 'Regulatory inquiry (DPA, EHRC, etc.)' },
                  { value: 'criminal_investigation', label: 'Criminal investigation' },
                  { value: 'other',                label: 'Other (document below)' },
                ]}
                value={form.legalBasis}
                onChange={(v) => setForm({ ...form, legalBasis: v })}
              />
              <Input
                label="Expected release date (optional)"
                type="date"
                value={form.expectedReleaseDate}
                onChange={(e) => setForm({ ...form, expectedReleaseDate: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Reason <span className="text-destructive">*</span>
              </label>
              <textarea
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                rows={3}
                placeholder="e.g. Employment tribunal claim filed 2025-01-12, case ref ET/12345/2025. Records must be preserved until hearing concluded and any appeal window closed."
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="default" size="sm" onClick={handlePlace}>
                Place hold
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {!canPlace && (
          <div className="mt-3 text-xs bg-warning/10 border border-warning/30 rounded p-3 text-warning">
            <Icon name="Info" size={12} className="inline mr-1" />
            Only admins and approvers can place legal holds. Your role is read-only.
          </div>
        )}
      </div>

      {/* Active holds */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border">
          <h3 className="font-semibold text-foreground">
            Active Holds <span className="ml-1 text-sm text-muted-foreground">({activeHolds.length})</span>
          </h3>
        </div>
        {activeHolds.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            No active legal holds. All subjects are subject to normal retention policy.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {activeHolds.map((h) => (
              <HoldCard
                key={h.id}
                hold={h}
                canRelease={canPlace}
                isOwn={h.placedBy === currentUserId}
                onRelease={() => handleRelease(h)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Released holds */}
      {releasedHolds.length > 0 && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold text-foreground">
              Released Holds <span className="ml-1 text-sm text-muted-foreground">({releasedHolds.length})</span>
            </h3>
            <p className="text-xs text-muted-foreground">Audit trail of past holds.</p>
          </div>
          <div className="divide-y divide-border">
            {releasedHolds.map((h) => (
              <HoldCard key={h.id} hold={h} canRelease={false} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const HoldCard = ({ hold, canRelease, isOwn, onRelease }) => {
  const basisLabels = {
    litigation: 'Litigation',
    tax_audit: 'Tax audit',
    regulatory_inquiry: 'Regulatory inquiry',
    criminal_investigation: 'Criminal investigation',
    other: 'Other',
  };
  const isActive = hold.status === 'active';

  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <p className="font-medium text-foreground">{hold.subjectEmail}</p>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              isActive ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'
            }`}>
              {hold.status}
            </span>
            <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">
              {basisLabels[hold.legalBasis] || hold.legalBasis}
            </span>
          </div>
          <p className="text-sm text-muted-foreground italic mb-2">"{hold.reason}"</p>
          <p className="text-xs text-muted-foreground">
            Placed by <strong>{hold.placedBy}</strong> on {new Date(hold.placedAt).toLocaleDateString('en-GB')}
            {hold.expectedReleaseDate && (
              <> · expected release: {new Date(hold.expectedReleaseDate).toLocaleDateString('en-GB')}</>
            )}
          </p>
          {!isActive && hold.releasedBy && (
            <p className="text-xs text-muted-foreground mt-1">
              Released by <strong>{hold.releasedBy}</strong> on {new Date(hold.releasedAt).toLocaleDateString('en-GB')}
              {hold.releaseNote && <>, "{hold.releaseNote}"</>}
            </p>
          )}
        </div>
        {isActive && canRelease && !isOwn && (
          <Button variant="outline" size="sm" iconName="Unlock" onClick={onRelease}>
            Release
          </Button>
        )}
        {isActive && isOwn && (
          <span className="text-xs px-2 py-1 bg-warning/10 text-warning rounded">
            Different user must release (separation of duties)
          </span>
        )}
      </div>
    </div>
  );
};

export default AdminConsolePage;
